import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  ShoppingCart, 
  Leaf, 
  ArrowRight,
  TrendingUp,
  Eye,
  LogOut
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { categoryAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState({
    totalProducts: 0,
    categories: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const categories = await categoryAPI.getAll();
      
      const totalProducts = categories.reduce((sum, cat) => sum + cat.count, 0);
      
      setStats({
        totalProducts,
        categories
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast({
      title: "Logged out",
      description: "You have been logged out successfully"
    });
    navigate('/admin/login');
  };

  return (
    <div className="admin-dashboard-container">
      {/* Header */}
      <div className="admin-header">
        <div className="header-content">
          <div className="dashboard-title-section">
            <Leaf size={40} className="dashboard-icon" />
            <div>
              <h1 className="dashboard-title">The LaShay & Silk Collective</h1>
              <p className="dashboard-subtitle">Admin Dashboard</p>
            </div>
          </div>
          <div className="header-actions">
            <span className="user-info">Welcome, {user?.username}</span>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="view-site-btn"
            >
              <Eye size={18} className="mr-2" />
              View Site
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="logout-btn"
            >
              <LogOut size={18} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="admin-content">
        {/* Stats Cards */}
        <div className="stats-grid">
          <Card className="stat-card total-products">
            <CardContent className="stat-card-content">
              <div className="stat-icon">
                <Package size={32} />
              </div>
              <div className="stat-info">
                <p className="stat-label">Total Products</p>
                <p className="stat-value">{stats.totalProducts}</p>
              </div>
              <TrendingUp className="stat-trend" size={20} />
            </CardContent>
          </Card>

          <Card className="stat-card total-categories">
            <CardContent className="stat-card-content">
              <div className="stat-icon">
                <ShoppingCart size={32} />
              </div>
              <div className="stat-info">
                <p className="stat-label">Categories</p>
                <p className="stat-value">{stats.categories.length}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="quick-actions-card">
          <CardContent className="quick-actions-content">
            <h2 className="section-title">Quick Actions</h2>
            <div className="quick-actions-grid">
              <Button
                onClick={() => navigate('/admin/products')}
                className="quick-action-btn manage"
              >
                <Package size={24} />
                <span>Manage Products</span>
                <ArrowRight size={18} />
              </Button>
              <Button
                onClick={() => navigate('/admin/products/new')}
                className="quick-action-btn add"
              >
                <Package size={24} />
                <span>Add New Product</span>
                <ArrowRight size={18} />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Categories Overview */}
        <Card className="categories-overview-card">
          <CardContent className="categories-overview-content">
            <h2 className="section-title">Categories Overview</h2>
            
            {loading ? (
              <div className="loading-state">
                <p>Loading categories...</p>
              </div>
            ) : (
              <div className="categories-list">
                {stats.categories.map((category, index) => (
                  <div key={index} className="category-item">
                    <div className="category-info">
                      <h3 className="category-name">{category.category}</h3>
                      <p className="category-count">{category.count} products</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate('/admin/products')}
                      className="view-category-btn"
                    >
                      View <ArrowRight size={16} className="ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
